// [방문자 수 2026-07-12] 커뮤니티 > 방문자 수 대시보드.
// /request/getVisitorStats 를 읽어 씨네트리·영화거래소 방문자를 시간대별/날짜별로 차트·표시.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import dayjs from 'dayjs';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const SITE_LABEL = { cinetree: '씨네트리', exchange: '영화거래소' };
const SITE_COLOR = { cinetree: '#4e79a7', exchange: '#f28e2b' };
const SITES = ['cinetree', 'exchange'];

export default function VisitorStats() {
  const [mode, setMode] = useState('hour');                                  // 'hour'(하루 시간대별) | 'date'(기간 날짜별)
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [from, setFrom] = useState(dayjs().subtract(13, 'day').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [metric, setMetric] = useState('uv');                                // 'uv'(고유 방문자) | 'pv'(페이지뷰)
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = mode === 'hour'
        ? `/request/getVisitorStats?granularity=hour&from=${date}`
        : `/request/getVisitorStats?granularity=date&from=${from}&to=${to}`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (e) { console.error('방문자 통계 로드 실패', e); setRows([]); }
    finally { setLoading(false); }
  }, [mode, date, from, to]);

  useEffect(() => { load(); }, [load]);

  // 라벨(축)
  let labels;
  if (mode === 'hour') {
    labels = Array.from({ length: 24 }, (_, h) => `${h}시`);
  } else {
    const n = Math.max(0, Math.min(366, dayjs(to).diff(dayjs(from), 'day')));
    labels = Array.from({ length: n + 1 }, (_, i) => dayjs(from).add(i, 'day').format('YYYY-MM-DD'));
  }

  const datasets = SITES.map((site) => {
    const map = {};
    rows.filter((r) => r.site === site).forEach((r) => { map[String(r.bucket)] = Number(r[metric]) || 0; });
    const data = mode === 'hour'
      ? Array.from({ length: 24 }, (_, h) => map[String(h)] || 0)
      : labels.map((d) => map[d] || 0);
    return {
      label: SITE_LABEL[site],
      data,
      borderColor: SITE_COLOR[site],
      backgroundColor: SITE_COLOR[site] + '66',
      tension: 0.3,
    };
  });

  const chartData = { labels, datasets };
  const options = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: `${mode === 'hour' ? date + ' 시간대별 ' : '날짜별 '}${metric === 'uv' ? '고유 방문자' : '페이지뷰'}` },
    },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const totals = SITES.map((site) => ({
    site,
    sum: rows.filter((r) => r.site === site).reduce((a, r) => a + (Number(r[metric]) || 0), 0),
  }));

  return (
    <div style={{ width: '100%', padding: '20px', overflow: 'auto' }}>
      <h2 style={{ marginBottom: 12 }}>방문자 수</h2>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <select value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="hour">시간대별(하루)</option>
          <option value="date">날짜별(기간)</option>
        </select>
        {mode === 'hour'
          ? <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          : (<>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              <span>~</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </>)}
        <select value={metric} onChange={(e) => setMetric(e.target.value)}>
          <option value="uv">고유 방문자</option>
          <option value="pv">페이지뷰</option>
        </select>
        <button onClick={load}>조회</button>
        {loading && <span style={{ color: '#888' }}>불러오는 중…</span>}
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        {totals.map((t) => (
          <div key={t.site} style={{ border: '1px solid #eee', borderRadius: 8, padding: '10px 16px', minWidth: 160 }}>
            <div style={{ color: '#888', fontSize: 12 }}>{SITE_LABEL[t.site]} · {metric === 'uv' ? '고유 방문자' : '페이지뷰'} 합계</div>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: SITE_COLOR[t.site] }}>{t.sum.toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div style={{ maxWidth: 1000 }}>
        {mode === 'hour' ? <Bar data={chartData} options={options} /> : <Line data={chartData} options={options} />}
      </div>

      <div style={{ color: '#999', fontSize: 12, marginTop: 14, lineHeight: 1.6 }}>
        * 쿠키 기반 <b>고유 방문자</b>(대략치)입니다. 데이터는 도입 시점부터 누적되며, 광고차단·자바스크립트 차단 방문자는 집계되지 않습니다.<br />
        * 시간은 한국시간(KST) 기준. 봇·프리렌더는 제외됩니다.
      </div>
    </div>
  );
}
